/**
 * App Component — Root Application
 * Sets up routing, authentication, and layout structure.
 * Routes:
 *  - /login — public login page
 *  - / — protected dashboard with nested routes
 *    - /members — user management (create/block/delete)
 *    - /teams — teams overview
 *    - /teams/:teamId — team detail (roles, owner, members)
 *    - /roles — roles management
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import DashboardLayout from './components/DashboardLayout/DashboardLayout';
import Login from './pages/Login/Login';
import Members from './pages/Members/Members';
import Teams from './pages/Teams/Teams';
import TeamDetail from './pages/Teams/TeamDetail';
import Roles from './pages/Roles/Roles';
import './App.css';

/**
 * ProtectedRoute — redirects to /login if no user session exists.
 * Shows a loading spinner while checking the session.
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // Show loading state while checking session
  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * PublicRoute — redirects to / if user is already logged in.
 * Prevents logged-in users from seeing the login page.
 */
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner"></div>
      </div>
    );
  }

  // Redirect logged-in users to the dashboard
  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Main App component wrapped with AuthProvider and Router.
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public route — login page */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Protected routes — dashboard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Default redirect: / → /members */}
            <Route index element={<Navigate to="/members" replace />} />
            <Route path="members" element={<Members />} />
            <Route path="teams" element={<Teams />} />
            <Route path="teams/:teamId" element={<TeamDetail />} />
            <Route path="roles" element={<Roles />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
